import axios from "axios";
import TrustdRequest from "../model/TrustdRequest";
import TrustdResponse from "../model/TrustdResponse";
import TrustdInvitation from "../model/TrustdInvitation";
import TrustdUser from "../model/TrustdUser";
import AuthService from "./AmplifyAuth";
import TrustdPayload from "../model/TrustdPayload";
import GuestFormRequest from "../model/GuestFormRequest";
import { resolveOverallScreeningStatus } from "../utils/BackgroundScreening";

const OWNERREZ_API_ADDRESS = "/v1/integrations/tpc/ownerrez/api";
const HOSTAWAY_API_ADDRESS = "/v1/integrations/tpc/hostaway/api";
const HOSTAWAY_API_SIGNUP = "/signup";
const OWNERREZ_API_SIGNUP = "/signup";
//const OWNERREZ_API_GET_USER = '/get-user'
const TRUSTD_API_ADDRESS = window.location.origin;
const TRUSTD_API_VERSION = "/v1";
const TRUSTD_API_MATCH = "/match";
const TRUSTD_API_INVITATION = "/client/invitation";
const TRUSTD_API_USERS = "/users";
const TRUSTD_API_MATCH_BY_HOST_EMAIL = "/match/byhostemail";
const TRUSTD_API_MATCH_BY_HOST_EMAIL_FILTERED = "/match/byhostemail/filtered";
const TRUSTD_API_MATCH_BY_HOST_EMAIL_AND_REFERENCE =
  "/match/byhostemailandreference";
const TRUSTD_API_MATCH_SHARED = "/match/shared";
const TRUSTD_API_MATCH_SHARE_LINK = "/match/share-link";
const TRUSTD_API_MATCH_REVIEW = "/match/review";
const TRUSTD_API_MATCH_DASHBOARD_BY_HOST_EMAIL = "/match/dashboard";
const TRUSTD_API_GUEST_FORM = "/guestForm";
const TRUSTD_API_GUEST_FORM_BY_ID = "/guestForm/id";
const TRUSTD_API_GUEST_FORM_BY_MATCH_REQUEST_ID = "/guestForm/matchRequestId";
const TRUSTD_API_GUEST_FORM_BY_HOST_EMAIL = "/guestForm/byHostEmail";
const TRUSTD_API_GUEST_FORM_SEND_EMAIL = "/guestForm/sendGuestFormEmail";
const DASHBOARD_CACHE_TTL_MS = 60 * 1000;
const LISTINGS_CACHE_VERSION = 1;
const LISTINGS_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const userInfoCacheByHost = new Map();
const userInfoInFlightByHost = new Map();
const dashboardCacheByHost = new Map();
const dashboardInFlightByHost = new Map();

function readCache(cache, key, ttlMs) {
  const entry = cache.get(key);
  if (!entry?.value || !entry?.cachedAt) return null;
  if (Date.now() - entry.cachedAt > ttlMs) return null;
  return entry.value;
}

function writeCache(cache, key, value) {
  if (value == null) return;
  cache.set(key, {
    value,
    cachedAt: Date.now(),
  });
}

const listingsCacheKey = (email) =>
  `trustd:listings:v${LISTINGS_CACHE_VERSION}:${String(email || "").trim().toLowerCase()}`;

const enforceRequiredScreening = (listings = []) =>
  listings.map((listing) => ({ ...listing, isScreeningEnabled: true }));

const readListingsCache = (email, maxAgeMs = LISTINGS_CACHE_MAX_AGE_MS) => {
  try {
    const cached = JSON.parse(localStorage.getItem(listingsCacheKey(email)) || "null");
    if (!cached?.listings || !Array.isArray(cached.listings)) return null;
    if (Date.now() - Number(cached.cachedAt || 0) > maxAgeMs) return null;
    return { ...cached, listings: enforceRequiredScreening(cached.listings) };
  } catch { return null; }
};

const writeListingsCache = (email, listings) => {
  try {
    localStorage.setItem(listingsCacheKey(email), JSON.stringify({ listings: enforceRequiredScreening(listings), cachedAt: Date.now() }));
  } catch { /* Cache availability must never block the portal. */ }
};

const readApiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data === "object") {
    if (typeof data.message === "string" && data.message.trim()) return data.message.trim();
    if (typeof data.detail === "string" && data.detail.trim()) return data.detail.trim();
    if (typeof data.error === "string" && data.error.trim()) return data.error.trim();
    if (data.error && typeof data.error === "object") {
      if (typeof data.error.message === "string" && data.error.message.trim()) {
        return data.error.message.trim();
      }
      if (typeof data.error.code === "string" && data.error.code.trim()) {
        return data.error.code.trim();
      }
    }
  }
  if (typeof error?.message === "string" && error.message.trim()) return error.message.trim();
  return "";
};

export default class TrustdService {
  static async getAuthorizedApiHeaders() {
    const session = await AuthService.currentSession();
    const accessToken = session?.accessToken;
    if (!accessToken) {
      throw new Error("Missing host session.");
    }
    return {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
      Authorization: `Bearer ${accessToken}`,
    };
  }

  static async callMatchApi(guest, host) {
    const trustdPayload = TrustdRequest.create(guest, host);

    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2", // todo
    };

    const apiUrl = TRUSTD_API_ADDRESS + TRUSTD_API_VERSION + TRUSTD_API_MATCH;
    console.log("apiUrl: ", apiUrl);

    const response = await axios.post(apiUrl, trustdPayload, {
      headers: headers,
    });
    return TrustdResponse.create(response.data, trustdPayload);
  }

  static async refreshMatchApiResultsResponse(trustdReference) {
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_MATCH +
      "/" +
      trustdReference;
    console.log("apiUrl: ", apiUrl);

    const response = await axios.get(apiUrl, { headers: headers });
    return TrustdResponse.create(response.data);
  }

  static async verifyInvitationCode(invitationCode, email) {
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const params =
      typeof email === "string" && email.trim().length > 0
        ? `?email=${encodeURIComponent(email.trim().toLowerCase())}`
        : "";

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_INVITATION +
      "/" +
      invitationCode +
      params;
    console.log("apiUrl: ", apiUrl);

    const response = await axios.get(apiUrl, { headers: headers });
    return TrustdInvitation.create(response.data);
  }

  static async saveUserInfo(userForm) {
    const userPayload = TrustdUser.createFromUserForm(userForm);
    const normalizedEmail = String(userForm.email || userForm.username || "").trim().toLowerCase();
    const normalizedChannel = String(userForm.channel || "").trim().toLowerCase();
    const normalizedAccountId = String(userForm.accountId || "").trim();
    const normalizedApiKey = String(userForm.apiKey || "").trim();
    const hostawayPayload = {
      clientId: normalizedAccountId,
      apiKey: normalizedApiKey,
      email: normalizedEmail,
      name: userForm.contactName,
      company: userForm.company,
      phone: userForm.phoneNumber,
      screening: userForm.screening,
      freeScreening: userForm.freeScreening,
      status: userForm.status,
      terms: "on",
      sendEmail: "YES",
    };

    console.log("userPayload", userPayload);

    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl = TRUSTD_API_ADDRESS + TRUSTD_API_VERSION + TRUSTD_API_USERS;
    const hostawayApiUrl = HOSTAWAY_API_ADDRESS + HOSTAWAY_API_SIGNUP;
    console.log("apiUrl: ", apiUrl);

    const shouldValidateHostaway = normalizedChannel === "hostaway" && normalizedAccountId !== "" && normalizedApiKey !== "";
    if (shouldValidateHostaway) {
      let hostawaySignupResponse;
      try {
        hostawaySignupResponse = await axios.post(hostawayApiUrl, hostawayPayload, { headers: headers });
      } catch (error) {
        const detail = readApiErrorMessage(error);
        throw new Error(detail || "Hostaway credentials could not be validated.");
      }
      const hostawaySignupStatus = String(hostawaySignupResponse?.data?.status || "").trim().toLowerCase();
      if (hostawaySignupStatus !== "success") {
        const detail = String(hostawaySignupResponse?.data?.message || "").trim();
        throw new Error(detail || "Hostaway credentials could not be validated.");
      }

      const hostawayLookupResponse = await axios.get(
        `/v1/integrations/tpc/hostaway/user/${encodeURIComponent(normalizedEmail)}`,
        { headers: headers, timeout: 0 },
      );
      const lookupPayload = hostawayLookupResponse?.data;
      const lookupStatus = typeof lookupPayload === "object" && lookupPayload !== null
        ? String(lookupPayload.status || "").trim().toLowerCase()
        : "";
      if (lookupPayload === "" || lookupStatus === "error") {
        throw new Error("Hostaway connection verification failed. Please re-check Account ID and API Key.");
      }
    }

    await axios.post(apiUrl, userPayload, { headers: headers });

    const cacheKey = normalizedEmail;
    if (cacheKey) {
      userInfoCacheByHost.delete(cacheKey);
      userInfoInFlightByHost.delete(cacheKey);
      dashboardCacheByHost.delete(cacheKey);
      dashboardInFlightByHost.delete(cacheKey);
    }

    // do not return
  }

  static async getScoreByHostEmailAndReference(hostEmail, trustdReference) {
    console.log(`hostEmail ${hostEmail} and reference ${trustdReference}`);

    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_MATCH_BY_HOST_EMAIL_AND_REFERENCE +
      "/" +
      encodeURIComponent(hostEmail.toLowerCase()) +
      "/" +
      encodeURIComponent(trustdReference);
    console.log("apiUrl: ", apiUrl);

    const response = await axios.get(apiUrl, { headers: headers });
    console.log("response: ", response);
    return {
      payload: TrustdPayload.create(response.data.Payload),
      response: TrustdResponse.create(response.data.Response, response.data.Payload),
      createdAt: response.data.CreatedAt || response.data.created_at || null,
    };
  }

  static async getScoreBySharedToken(trustdReference, shareToken) {
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_MATCH_SHARED +
      "/" +
      encodeURIComponent(trustdReference) +
      "?st=" +
      encodeURIComponent(String(shareToken || ""));
    console.log("apiUrl: ", apiUrl);

    const response = await axios.get(apiUrl, { headers: headers });
    return {
      payload: TrustdPayload.create(response.data.Payload),
      response: TrustdResponse.create(response.data.Response, response.data.Payload),
      createdAt: response.data.CreatedAt || response.data.created_at || null,
    };
  }

  static async createQuickScoreShareLink(trustdReference, { expiresInMinutes = 60 } = {}) {
    const headers = await this.getAuthorizedApiHeaders();
    const apiUrl = TRUSTD_API_ADDRESS + TRUSTD_API_VERSION + TRUSTD_API_MATCH_SHARE_LINK;
    const response = await axios.post(
      apiUrl,
      { trustdReference, expiresInMinutes },
      { headers },
    );
    const token = String(response?.data?.token || "").trim();
    if (!token) {
      throw new Error("Share token could not be generated.");
    }
    return `${window.location.origin}/q/${encodeURIComponent(trustdReference)}?st=${encodeURIComponent(token)}`;
  }

  static async listReviewsByHostEmail() {
    const user = await AuthService.currentAuthenticatedUserAttributes();
    console.log("hostEmail", user.username);

    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_MATCH_BY_HOST_EMAIL +
      "/" +
      user.username.toLowerCase();
    console.log("apiUrl: ", apiUrl);

    const response = await axios.get(apiUrl, { headers: headers });
    console.log("response.data: ", response.data);
    return response.data;
  }

  static async listReviewsByHostEmailFiltered(page, pageSize, filters) {
    const user = await AuthService.currentAuthenticatedUserAttributes();
    console.log("hostEmail", user.username);

    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const body = {
      page: page,
      pageSize: pageSize,
      filters: filters || [],
    };

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_MATCH_BY_HOST_EMAIL_FILTERED +
      "/" +
      user.username.toLowerCase();
    console.log("apiUrl: ", apiUrl);

    const response = await axios
      .post(apiUrl, body, { headers: headers })
      .catch((error) => {
        console.log("list reviews by hostEmail filtered error:", error);
        return {
          data: {
            Data: [],
            TotalDocuments: 0,
            CurrentPage: 1,
            CurrentPageSize: 0,
            TotalPages: 1,
          },
        };
      });
    console.log("response.data: ", response.data);
    return response.data;
  }

  static async submitHostReview(payload) {
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_MATCH_REVIEW;
    console.log("apiUrl: ", apiUrl);

    const response = await axios.post(apiUrl, payload, { headers });
    return response.data;
  }

  static async saveGuestFormRequest(guest, booking, status, matchRequestId, options = {}) {
    const user = await AuthService.currentAuthenticatedUserAttributes();
    console.log("hostEmail", user.username);

    const rawData = {
      hostEmail: user.username.toLowerCase(),
      status: status,
    };
    if (arguments.length > 3) {
      rawData.matchRequestId = matchRequestId;
    }
    rawData.idVerificationRequired = Boolean(options.idVerificationRequired);
    rawData.selfieVerificationRequired = Boolean(options.selfieVerificationRequired);
    const guestFormPayload = GuestFormRequest.create(rawData, guest, booking);
    console.log("guestFormPayload", guestFormPayload);

    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl =
      TRUSTD_API_ADDRESS + TRUSTD_API_VERSION + TRUSTD_API_GUEST_FORM;
    console.log("apiUrl: ", apiUrl);

    const response = await axios.post(apiUrl, guestFormPayload, {
      headers: headers,
    });
    return response.data.Id;
  }

  static async getListings(hostEmail, { sync = true, activeOnly = false } = {}) {
    const headers = await this.getAuthorizedApiHeaders();
    const response = await axios.get(
      `${TRUSTD_API_ADDRESS}${TRUSTD_API_VERSION}/listings/${encodeURIComponent(hostEmail)}?sync=${sync}&active=${activeOnly}`,
      { headers, timeout: 0 },
    );
    const result = { ...response.data, listings: enforceRequiredScreening(response.data?.listings || []) };
    if (!activeOnly) writeListingsCache(hostEmail, result.listings || []);
    return result;
  }

  static getCachedListings(hostEmail) {
    return readListingsCache(hostEmail);
  }

  static applyListingsDefaultsToCache(hostEmail, settings) {
    const cached = readListingsCache(hostEmail);
    if (!cached) return;
    writeListingsCache(hostEmail, cached.listings.map((listing) => ({
      ...listing,
      isScreeningEnabled: true,
      isIdVerificationEnabled: Boolean(settings.isIdVerificationEnabled || settings.isSelfieVerificationEnabled),
      isSelfieVerificationEnabled: Boolean(settings.isSelfieVerificationEnabled),
    })));
  }

  static async applyVerificationDefaults(hostEmail, settings) {
    const headers = await this.getAuthorizedApiHeaders();
    const response = await axios.post(
      `${TRUSTD_API_ADDRESS}${TRUSTD_API_VERSION}/users/verification-defaults/${encodeURIComponent(hostEmail)}`,
      {
        isIdVerificationEnabled: Boolean(settings.isIdVerificationEnabled || settings.isSelfieVerificationEnabled),
        isSelfieVerificationEnabled: Boolean(settings.isSelfieVerificationEnabled),
      },
      { headers },
    );
    this.applyListingsDefaultsToCache(hostEmail, response.data);
    return response.data;
  }

  static async applyVerificationSetting(hostEmail, field, enabled) {
    const headers = await this.getAuthorizedApiHeaders();
    const response = await axios.post(
      `${TRUSTD_API_ADDRESS}${TRUSTD_API_VERSION}/users/verification-setting/${encodeURIComponent(hostEmail)}`,
      { field, enabled: Boolean(enabled) },
      { headers },
    );
    const cached = readListingsCache(hostEmail);
    if (cached) writeListingsCache(hostEmail, cached.listings.map((listing) => ({
      ...listing,
      isScreeningEnabled: true,
      ...(field === "id" ? {
        isIdVerificationEnabled: Boolean(enabled),
        ...(!enabled ? { isSelfieVerificationEnabled: false } : {}),
      } : {
        isSelfieVerificationEnabled: Boolean(enabled),
        ...(enabled ? { isIdVerificationEnabled: true } : {}),
      }),
    })));
    return response.data;
  }

  static async syncListings(hostEmail) {
    const headers = await this.getAuthorizedApiHeaders();
    const response = await axios.post(`${TRUSTD_API_ADDRESS}${TRUSTD_API_VERSION}/listings/${encodeURIComponent(hostEmail)}/sync`, {}, { headers, timeout: 0 });
    return response.data;
  }

  static async createListing(hostEmail, listing) {
    const headers = await this.getAuthorizedApiHeaders();
    const response = await axios.post(`${TRUSTD_API_ADDRESS}${TRUSTD_API_VERSION}/listings/${encodeURIComponent(hostEmail)}`, { ...listing, isScreeningEnabled: true }, { headers });
    const cached = readListingsCache(hostEmail);
    if (cached) writeListingsCache(hostEmail, [...cached.listings, response.data]);
    return response.data;
  }

  static async updateListing(hostEmail, listingId, patch) {
    const headers = await this.getAuthorizedApiHeaders();
    const response = await axios.patch(`${TRUSTD_API_ADDRESS}${TRUSTD_API_VERSION}/listings/${encodeURIComponent(hostEmail)}/${listingId}`, { ...patch, isScreeningEnabled: true }, { headers });
    const cached = readListingsCache(hostEmail);
    if (cached) writeListingsCache(hostEmail, cached.listings.map((item) => item.id === listingId ? response.data : item));
    return response.data;
  }

  static async deleteListing(hostEmail, listingId) {
    const headers = await this.getAuthorizedApiHeaders();
    await axios.delete(`${TRUSTD_API_ADDRESS}${TRUSTD_API_VERSION}/listings/${encodeURIComponent(hostEmail)}/${listingId}`, { headers });
    const cached = readListingsCache(hostEmail);
    if (cached) writeListingsCache(hostEmail, cached.listings.filter((item) => item.id !== listingId));
  }

  static async updateGuestFormRequest(
    id,
    guest,
    booking,
    status,
    scoreReference,
    hostEmail
  ) {
    if (id == null || id === "") {
      console.log("id required");
      return;
    }

    const rawData = {
      hostEmail: hostEmail.toLowerCase(),
      status: status,
    };
    if (scoreReference) {
      rawData.scoreReference = scoreReference;
      rawData.scoreStatus = "completed";
    }
    const guestFormPayload = GuestFormRequest.create(rawData, guest, booking);
    console.log("guestFormPayload", guestFormPayload);

    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_GUEST_FORM +
      "/" +
      id;
    console.log("apiUrl: ", apiUrl);

    const response = await axios.post(apiUrl, guestFormPayload, {
      headers: headers,
    });
    return response.data.Id;
  }

  static async updateGuestFormRequestGuest(id, guest) {
    if (id == null || id === "") {
      console.log("id required");
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const payload = {};
    payload.GuestInfo = {
      email: guest.email,
      phone: guest.phone,
      first_name: guest.firstName,
      middle_name: guest.middleName,
      last_name: guest.lastName,
      dob: guest.birthDate,
    };
    console.log("payload", payload);

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_GUEST_FORM +
      "/" +
      id;
    console.log("apiUrl: ", apiUrl);

    const response = await axios.post(apiUrl, payload, { headers: headers });
    return response.data.Id;
  }

  static async getGuestFormRequestById(id) {
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_GUEST_FORM_BY_ID +
      "/" +
      id;
    console.log("apiUrl: ", apiUrl);

    const response = await axios
      .get(apiUrl, { headers: headers })
      .catch((error) => {
        console.log("Error retrieving guest form request: " + error);
        return {
          guestFormRequest: null,
        };
      });
    console.log("response.data: ", response.data);

    if (response.status !== 200 || response.data.length === 0) {
      return {
        guestFormRequest: null,
      };
    }

    return {
      guestFormRequest: response.data,
    };
  }

  static async getGuestFormRequestByMatchRequestId(matchRequestId) {
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_GUEST_FORM_BY_MATCH_REQUEST_ID +
      "/" +
      matchRequestId;
    console.log("apiUrl: ", apiUrl);

    const response = await axios.get(apiUrl, { headers: headers });
    console.log("response.data: ", response.data);

    const rawData = {
      matchRequestId: response.data.MatchRequestId,
      hostEmail: response.data.HostEmail,
      status: response.data.Status,
    };

    return {
      guestFormRequest: GuestFormRequest.create(
        rawData,
        response.data.GuestInfo,
        response.data.BookingInfo
      ),
    };
  }

  static async listGuestFormRequestByHostEmail() {
    const user = await AuthService.currentAuthenticatedUserAttributes();
    console.log("hostEmail", user.username);

    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_GUEST_FORM_BY_HOST_EMAIL +
      "/" +
      user.username.toLowerCase();
    console.log("apiUrl: ", apiUrl);

    const response = await axios
      .get(apiUrl, { headers: headers })
      .catch((error) => {
        console.log("guestFormRequest by hostEmail error:", error);
        return [];
      });
    console.log("response.data: ", response.data);
    return response.data;
  }

  static async sendGuestFormEmail(guestFormId, guestFormUrl) {
    const payload = {
      guestFormUrl: guestFormUrl,
    };

    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_GUEST_FORM_SEND_EMAIL +
      "/" +
      guestFormId;
    console.log("apiUrl: ", apiUrl);

    try {
      const response = await axios.post(apiUrl, payload, { headers: headers });
      return {
        id: response?.data?.Id || guestFormId,
        email: response?.data?.email || null,
      };
    } catch (error) {
      return {
        id: null,
        errorData: error?.response?.data || { message: error?.message || "Unable to send guest form email" },
        statusCode: error?.response?.status || 500,
      };
    }
  }

  static async getDashboardInformationByHostEmail({ forceRefresh = false } = {}) {
    const user = await AuthService.currentAuthenticatedUserAttributes();
    console.log("hostEmail", user.username);
    const cacheKey = user.username.toLowerCase();

    if (!forceRefresh) {
      const cached = readCache(dashboardCacheByHost, cacheKey, DASHBOARD_CACHE_TTL_MS);
      if (cached) return cached;
      if (dashboardInFlightByHost.has(cacheKey)) return dashboardInFlightByHost.get(cacheKey);
    }

    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_MATCH_DASHBOARD_BY_HOST_EMAIL +
      "/" +
      user.username.toLowerCase();
    console.log("apiUrl: ", apiUrl);

    const request = axios
      .get(apiUrl, { headers: headers })
      .catch((error) => {
        console.log("dashboardInformaiton by hostEmail error:", error);
        return { data: [] };
      });
    dashboardInFlightByHost.set(cacheKey, request);

    const response = await request.finally(() => {
      dashboardInFlightByHost.delete(cacheKey);
    });
    console.log("response.data: ", response.data);
    writeCache(dashboardCacheByHost, cacheKey, response.data);
    return response.data;
  }

  static async getUserInformationByHostEmail({ forceRefresh = false } = {}) {
    const user = await AuthService.currentAuthenticatedUserAttributes();
    console.log("hostEmail", user.username);
    const cacheKey = user.username.toLowerCase();

    if (!forceRefresh && userInfoInFlightByHost.has(cacheKey)) {
      return userInfoInFlightByHost.get(cacheKey);
    }

    const apiUrl =
      TRUSTD_API_ADDRESS +
      TRUSTD_API_VERSION +
      TRUSTD_API_USERS +
      "/" +
      user.username.toLowerCase();
    console.log("apiUrl: ", apiUrl);

    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const request = axios.get(apiUrl, { headers });
    userInfoInFlightByHost.set(cacheKey, request);
    const response = await request.finally(() => {
      userInfoInFlightByHost.delete(cacheKey);
    });

    console.log("user info: ", response.data);
    userInfoCacheByHost.delete(cacheKey);
    return response.data;
  }

  static async ownerrezTempUserCreate(form) {
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl = OWNERREZ_API_ADDRESS + OWNERREZ_API_SIGNUP;

    return await axios.post(apiUrl, form, { headers });
  }

  static async redirectTokeet(email, type) {
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl =
      "/v1/integrations/stripe/pms/tokeetAuth/" +
      email +
      "/" +
      type;

    return await axios.get(apiUrl, { headers });
  }

  static async redirectHospitable(email) {
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };

    const apiUrl =
      "/v1/integrations/stripe/pms/hospitableAuth/" +
      email;

    return await axios.get(apiUrl, { headers });
  }

  static async saveTagForProvider(
    provider,
    idVerificationEnabled,
    idVerificationPass,
    idVerificationStatus,
    email,
    matchApiResponse,
    guest,
    booking
  ) {
    const normalizedProvider = String(provider || "").trim().toLowerCase();
    if (!["ownerrez", "hostaway", "hospitable"].includes(normalizedProvider)) {
      throw new Error(`Unsupported PMS provider: ${provider || "unknown"}`);
    }

    const headers = {
      "Content-Type": "application/json",
      "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
    };
    const payload = {
      email,
      score: resolveOverallScreeningStatus(matchApiResponse),
      screeningResults: {
        criminalRecords: matchApiResponse.criminalList,
        criminalRecordsDescription: matchApiResponse.criminalListDescription,
        sexOffender: matchApiResponse.sexOffenderList,
        sexOffenderDescription: matchApiResponse.sexOffenderListDescription,
        ofac: matchApiResponse.ofacList,
        ofacDescription: matchApiResponse.ofacListDescription,
        un: matchApiResponse.unList,
        unDescription: matchApiResponse.unListDescription,
        fbiWanted: matchApiResponse.fbiList,
      },
      trustdReference: matchApiResponse.trustdReference,
      guest,
      booking,
      idVerificationEnabled,
      idVerificationPass,
      idVerificationStatus,
    };
    const response = await axios.post(`/v1/integrations/tpc/${normalizedProvider}/tag`, payload, {
      headers,
      timeout: 0,
    });
    const responseStatus = String(response?.data?.status || "").trim().toLowerCase();
    if (responseStatus === "error") {
      throw new Error(response?.data?.message || `${normalizedProvider} screening field sync failed.`);
    }
    return response.data;
  }
}
